import { authQueries, scopedQueries, UseQueryOptionsObject } from 'src/api/queries/queries';
import { defaultOptions } from 'src/api/queries/queries.utils';
import { PaginatedResponse } from 'src/api/Client';
import { brandClient, BrandsTableResponse } from 'src/lib/services/api/brand-api';
import { useMakeQuery } from 'src/api/queries/useMakeQuery';
import { useMakeInfiniteQuery } from 'src/api/queries/useMakeInfiniteQuery';
import { ApiQueryObject } from 'src/lib/services/api-query-params';
import { reactQueryClient } from 'src/providers/ReactQueryClientProvider';

const baseBrandsQueryKey = [authQueries.brands, scopedQueries.table];

const invalidateBrandsQuery = () => {
  reactQueryClient.invalidateQueries(baseBrandsQueryKey);
};

const useBrandsQuery = (
  params: ApiQueryObject = {},
  options?: UseQueryOptionsObject<PaginatedResponse<BrandsTableResponse>>,
) => {
  return useMakeQuery<PaginatedResponse<BrandsTableResponse>>(
    [...baseBrandsQueryKey, params],
    () => brandClient.index({ params }),
    {
      staleTime: 60 * 1000,
      // networkMode 'always' is a property of THIS endpoint's failure mode, not of any one
      // caller, so it lives here rather than being remembered at each call site. React Query v4
      // defaults to 'online' and reads navigator.onLine, which a mobile WebView can report as
      // false at cold start on a perfectly good connection; no transition occurs, so no
      // `online` event ever arrives to correct it and the query sits `paused` forever — no
      // request, no error, no data. Everything waiting on a brand then waits forever, which is
      // the Media Library hanging on "Loading media library..." (#2661).
      //
      // Two callers had already been patched individually and a third would have reproduced it
      // in full. Declared before the spread so a caller can still override it deliberately.
      networkMode: 'always' as const,
      ...defaultOptions({
        auth: true,
        ...(options ?? {}),
      }),
    },
  );
};

type BrandsApiResponse = PaginatedResponse<BrandsTableResponse>;

interface InfiniteBrandsQueryParams extends ApiQueryObject {
  search?: string;
}

const useInfiniteBrandsQuery = (
  params: InfiniteBrandsQueryParams = {},
  options?: UseQueryOptionsObject<BrandsApiResponse>,
) => {
  const { search, ...restParams } = params;

  return useMakeInfiniteQuery<BrandsApiResponse>(
    {
      queryKey: [...baseBrandsQueryKey, search],
      queryFn: ({ signal, ...context }) => {
        return brandClient.index({
          params: {
            ...restParams,
            page: context.pageParam,
            limit: restParams.limit || 10,
            ...(search ? { search } : {}),
          },
          signal,
        });
      },
      getNextPageParam: (lastPage) => {
        return lastPage.meta.current_page < lastPage.meta.last_page
          ? lastPage.meta.current_page + 1
          : undefined;
      },
      staleTime: 60 * 1000,
      ...defaultOptions({
        auth: true,
        ...options,
      }),
    },
    params,
  );
};

export { baseBrandsQueryKey, useBrandsQuery, useInfiniteBrandsQuery, invalidateBrandsQuery };
